import { BigDecimal, Brand, Effect, pipe, Schema, SchemaGetter } from 'effect';

/**
 * Price - Branded BigDecimal type for monetary values
 *
 * This branded type ensures Price values are distinct from regular BigDecimal
 * at the type level, preventing accidental misuse of monetary values.
 *
 * @example
 * const price = Price.make(BigDecimal.fromNumber(10.99))
 * const anotherPrice = Price.make(BigDecimal.fromNumber(5.00))
 */
export type Price = BigDecimal.BigDecimal & Brand.Brand<'Price'>;

/**
 * Price branded constructor
 *
 * Use Price.make() to create branded Price values from BigDecimal
 */
export const Price = Brand.nominal<Price>();

/**
 * Zero price constant
 *
 * A reusable constant representing a price of 0.00
 */
export const zeroPrice = Price(BigDecimal.fromNumberUnsafe(0));

/**
 * PriceFromApi Schema - API integer to branded Price
 *
 * Backend API returns prices as integers with implicit scale of 2 decimal places:
 * - API returns: `1000` (integer)
 * - Represents: `10.00` (2 decimal places)
 * - Converts to: Branded Price with BigDecimal for precise calculations
 *
 * @example
 * // Decode: API integer -> Price
 * Schema.decodeEffect(PriceFromApiSchema)(1000) // Price(BigDecimal("10.00"))
 *
 * // Encode: Price -> API integer
 * Schema.encodeEffect(PriceFromApiSchema)(price) // 1000
 */
// v4 removed `Schema.BigDecimalFromNumber`. Compose the conversion manually:
// API integer (cents) → BigDecimal (currency units) → branded Price. The
// `Schema.decodeTo(target, transformation)` shape takes `{decode, encode}` as
// `Getter`s (was `SchemaTransformation.transform(...)` in v3).
export const PriceFromApiSchema = Schema.Finite.pipe(
  Schema.decodeTo(Schema.BigDecimal, {
    decode: SchemaGetter.transform((apiPrice: number) =>
      BigDecimal.fromNumberUnsafe(apiPrice / 100)
    ),
    encode: SchemaGetter.transform((bd: BigDecimal.BigDecimal) =>
      Math.round(BigDecimal.toNumberUnsafe(bd) * 100)
    ),
  }),
  Schema.fromBrand('Price', Price)
).annotate({ identifier: 'PriceFromApiSchema' });

/**
 * Decodes an API price (integer in cents) to a Price, falling back to zero on parse error
 *
 * @example
 * const price = decodePriceOrZero(1000) // Price(BigDecimal("10.00"))
 * const invalid = decodePriceOrZero(NaN) // zeroPrice
 *
 * @param apiPrice - API price value in cents
 * @returns Decoded Price or zeroPrice on parse error
 */
export const decodePriceOrZero = (apiPrice: number): Price =>
  pipe(
    Schema.decodeEffect(PriceFromApiSchema)(apiPrice),
    Effect.catchTag('SchemaError', () => Effect.succeed(zeroPrice)),
    Effect.runSync
  );
